package com.dotdrives.web.pages.admin

import com.dotdrives.client.AdminClient
import com.dotdrives.core.uuidV7
import com.dotdrives.model.OrgSummary
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.serialization.json.buildJsonObject
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.dom.aria.AriaRole
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.time.Clock

/** One session monitor feeds both navigation presentations, including while the mobile menu is closed. */
data class CustomerAccessState(
    val org: OrgSummary?,
    val busy: Boolean = false,
    val error: String? = null,
    val onEndSession: () -> Unit = {},
) {
    val visible: Boolean get() = org?.let { it.supportSession != null || it.readOnly } == true
    val compactLabel: String get() = when {
        org?.supportSession == null -> "Read only"
        org.readOnly -> "Read-only support"
        else -> "Write support"
    }
}

fun useCustomerAccess(org: OrgSummary?): CustomerAccessState {
    val session = org?.supportSession
    val (scope) = useState { MainScope() }
    val (busy, setBusy) = useState(false)
    val (error, setError) = useState<String?>(null)
    useEffect(org?.id, session?.id, org?.readOnly) {
        setBusy(false)
        setError(null)
        if (org != null) {
            scope.launch {
                var ticks = 0
                while (true) {
                    delay(1000)
                    if (session?.expired(Clock.System.now()) == true) { window.location.hash = "#/admin"; break }
                    ticks++
                    if (ticks % 15 == 0) {
                        try {
                            val current = AdminClient().orgAccess(org.id)
                            if (current.session?.id != session?.id || current.readOnly != org.readOnly) {
                                if (session != null && current.session == null) window.location.hash = "#/admin" else window.location.reload()
                                break
                            }
                        } catch (e: CancellationException) { throw e }
                        catch (e: Throwable) { if (session != null) { window.location.hash = "#/admin"; break } }
                    }
                }
            }
        }
        try { awaitCancellation() } finally { scope.coroutineContext.cancelChildren() }
    }
    useEffect(scope) { try { awaitCancellation() } finally { scope.cancel() } }
    return CustomerAccessState(org, busy, error) {
        if (session != null && !busy) {
            setBusy(true); setError(null)
            scope.launch {
                try {
                    AdminClient().write("session.end", null, buildJsonObject {}, "End customer support session", uuidV7())
                    window.location.hash = "#/admin"
                } catch (e: CancellationException) { throw e }
                catch (e: Throwable) { setError("Could not confirm session end. Try again.") }
                finally { setBusy(false) }
            }
        }
    }
}

external interface CustomerAccessBannerProps : Props { var state: CustomerAccessState }

/** Ordinary navigation content: no sticky positioning or page-wide stacking layer. */
val CustomerAccessBanner = FC<CustomerAccessBannerProps> { props ->
    val state = props.state
    val org = state.org
    if (state.visible && org != null) {
        val session = org.supportSession
        div {
            className = ClassName("admin-session-card")
            role = "status".unsafeCast<AriaRole>()
            if (session != null) {
                span { className = ClassName("admin-session-mode"); +if (org.readOnly) "Read-only support" else "Audited write access" }
                span { className = ClassName("admin-session-org"); +org.name }
                span { +"${session.email ?: "Dot staff"} supporting this organization" }
                span { +"Ends ${session.expiresAt.toString().substring(11, 16)} UTC" }
            } else {
                span { className = ClassName("admin-session-mode"); +"Organization is read only" }
                span { className = ClassName("admin-session-org"); +org.name }
                span { +"Public giving, donor access and essential receipts continue." }
                if (com.dotdrives.model.canAdminister(org.role)) button {
                    type = ButtonType.buttonType
                    onClick = { window.location.hash = com.dotdrives.web.organizationRouteHash(org.id, "#/settings/billing") }
                    +"Open billing"
                } else span { +"Ask an owner or administrator to review billing." }
            }
            state.error?.let { message -> span { className = ClassName("admin-session-error"); +message } }
            if (session != null) button {
                type = ButtonType.buttonType
                disabled = state.busy
                onClick = { state.onEndSession() }
                +if (state.busy) "Ending…" else "End support session"
            }
        }
    }
}
